// === useConnectionMonitor — polls /health, listens to browser online/offline,
//     pauses when tab is hidden, writes results into the global connection store. ===

import { useEffect } from 'react';
import { useConnectionStore } from '../lib/connection-store';

const HEALTHY_INTERVAL_MS = 10_000;
const FAILURE_INTERVAL_MS = 30_000;
const REQUEST_TIMEOUT_MS = 4_000;

export function useConnectionMonitor(): void {
  const setStatus = useConnectionStore((s) => s.setStatus);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const state: { cancelled: boolean } = { cancelled: false };

    const schedule = (ms: number): void => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { void check(); }, ms);
    };

    const check = async (): Promise<void> => {
      if (state.cancelled) return;

      if (typeof document !== 'undefined' && document.hidden) {
        // Skip while tab is backgrounded — visibilitychange handler will resume.
        return;
      }

      const browserOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
      if (!browserOnline) {
        setStatus({ online: false, apiReachable: false, lastChecked: new Date() });
        schedule(FAILURE_INTERVAL_MS);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      let apiOk = false;
      try {
        const res = await fetch('/health', {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
        });
        apiOk = res.ok;
      } catch {
        apiOk = false;
      } finally {
        clearTimeout(timeoutId);
      }

      // Effect cleanup may flip state.cancelled while the await above is in flight;
      // eslint's flow analysis can't see this, hence the disable.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (state.cancelled) return;

      setStatus({ online: true, apiReachable: apiOk, lastChecked: new Date() });
      schedule(apiOk ? HEALTHY_INTERVAL_MS : FAILURE_INTERVAL_MS);
    };

    const handleOnline = (): void => {
      setStatus({ online: true });
      void check();
    };

    const handleOffline = (): void => {
      setStatus({ online: false, apiReachable: false, lastChecked: new Date() });
    };

    const handleVisibility = (): void => {
      if (typeof document === 'undefined') return;
      if (!document.hidden) {
        void check();
      } else if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);

    void check();

    return () => {
      state.cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [setStatus]);
}
